import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// 383: posuvný podklad vybrané záložky (laboratoř „Proyekty v pohybu“) — dojede s pružinou jako v iOS.
// Kontejner dostane `ref` (a `relative`), vybrané tlačítko `data-pill-on="true"` (nebo vlastní `selector`,
// např. `[aria-current="page"]` u NavLink), podklad se vykreslí přes `pill(className)`; 385: `pill(cls, { underline: true })`
// = podtržení 2 px u spodní hrany (záložky Ekonomiky, Nastavení). Dokud se nezměří (první snímek, skrytá lišta,
// testy), drží barvu samo tlačítko — `ready` je false.
export function useSlidingPill(value, extra = "", { selector = '[data-pill-on="true"]' } = {}) {
  const ref = useRef(null);
  const [box, setBox] = useState(null);
  const measure = useCallback(() => {
    const el = ref.current?.querySelector(selector);
    setBox(el && el.offsetWidth ? { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight } : null);
  }, [selector]);
  useLayoutEffect(measure, [value, extra, measure]);
  useEffect(() => {
    const host = ref.current;
    if (!host || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(host);
    Array.from(host.children).forEach((c) => ro.observe(c));
    return () => ro.disconnect();
  }, [measure, extra]);
  const pill = (className = "bg-brand", { underline = false } = {}) => (box ? (
    <span aria-hidden="true" className={`${underline ? "pp-underline" : "pp-pill"} ${className}`}
      style={underline ? { width: box.w, transform: `translate(${box.x}px, ${box.y + box.h - 2}px)` }
        : { width: box.w, height: box.h, transform: `translate(${box.x}px, ${box.y}px)` }} />
  ) : null);
  return { ref, ready: !!box, pill };
}

// 385: totéž bez úprav vykreslování pro drobné přepínače (Vše / Odchozí / Příchozí, Směna / Místo, Čára / Sloupce…):
// `ref={autoPill}` na kontejner. Vybrané tlačítko (aria-pressed / aria-selected / aria-checked / data-state=active /
// data-pill-on) půjčí podkladu svou barvu, stín, zaoblení a rámeček; pak je samo průhledné a podklad pod ním
// dojede (CSS .pp-autopill). Sleduje změny atributů a velikosti; bez rozměrů (skryté, testy) nedělá nic.
const ACTIVE = ':scope > :is([aria-pressed="true"],[aria-selected="true"],[aria-checked="true"],[data-state="active"],[data-pill-on="true"])';
const wired = typeof WeakSet !== "undefined" ? new WeakSet() : new Set();
export function autoPill(host) {
  if (!host || wired.has(host) || typeof MutationObserver === "undefined") return;
  wired.add(host);
  host.classList.add("pp-autopill");
  let pill = null;
  let dark = null;
  const paint = (on) => {
    host.classList.remove("pp-autopill-ready");          // barvu čte z tlačítka bez potlačení
    const cs = getComputedStyle(on);
    pill.style.background = cs.backgroundColor;
    pill.style.boxShadow = cs.boxShadow;
    pill.style.borderRadius = cs.borderTopLeftRadius;
    pill.style.border = parseFloat(cs.borderTopWidth) ? `${cs.borderTopWidth} solid ${cs.borderTopColor}` : "0";
    dark = document.documentElement.classList.contains("dark");
    host.classList.add("pp-autopill-ready");
  };
  const place = () => {
    let on = null;
    try { on = host.querySelector(ACTIVE); } catch { return; }
    if (!on || !on.offsetWidth) { if (pill) pill.style.opacity = "0"; return; }
    const first = !pill;
    if (first) {
      pill = document.createElement("span");
      pill.className = "pp-autopill-el";
      pill.setAttribute("aria-hidden", "true");
      host.insertBefore(pill, host.firstChild);
    }
    if (first || dark !== document.documentElement.classList.contains("dark")) paint(on);
    if (first) pill.style.transition = "none";
    pill.style.width = `${on.offsetWidth}px`;
    pill.style.height = `${on.offsetHeight}px`;
    pill.style.transform = `translate(${on.offsetLeft}px, ${on.offsetTop}px)`;
    pill.style.opacity = "1";
    if (first) { void pill.offsetWidth; pill.style.transition = ""; }
  };
  const tick = () => requestAnimationFrame(place);
  tick();
  new MutationObserver(tick).observe(host, { subtree: true, attributes: true, attributeFilter: ["aria-pressed", "aria-selected", "aria-checked", "data-state", "data-pill-on"] });
  if (typeof ResizeObserver !== "undefined") new ResizeObserver(tick).observe(host);
}
